import React, { useEffect, useState, useCallback, useMemo } from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { useSelector } from 'react-redux';
import { Grid, makeStyles } from '@material-ui/core';
import { Checkbox, FormControlLabel, Menu, MenuItem } from '@mui/material';
import ShowProgress from '../../ui/Backdrop';
import Theme from '../../ui/Theme';
import useCategory from '../hooks/useCategory';
import SearchBox from '../../shared/SearchBox';

const useStyles = makeStyles({
  root: {
    height: '54vh',
    overflowY: 'auto',
    backgroundColor: Theme.palette.background.paper,
    '&::-webkit-scrollbar': { width: '10px' },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 5px grey',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'lightGrey',
      borderRadius: '100px',
    },
  },
  searchBar: {
    maxHeight: '5vh',
    margin: '10px 0',
    padding: '4px',
  },
  searchText: {
    width: '93%',
    height: '42px',
    background: '#FAFAFA',
    border: '2px solid #E2E2E2',
    padding: '8px',
    fontFamily: 'Montserrat',
    fontWeight: '500',
    fontSize: '14px',
  },
  featherPlus: {
    width: '36px',
    height: '42px',
    background: '#FAFAFA',
  },
  featherPlusSelected: {
    width: '36px',
    height: '42px',
    background: '#D3D3D3',
  },
  listTextSelected: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '17px',
    fontStyle: 'normal',
    padding: '8px 16px 0px 0px',
    maxWidth: '100%',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listTextNonSelected: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '17px',
    fontStyle: 'normal',
    maxWidth: '100%',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listSubTextSelected: {
    color: Theme.palette.primary.main,
    fontSize: '12px',
    fontWeight: 500,
    fontStyle: 'normal',
    padding: '0px 16px 8px 0px',
  },
  listSubTextNonSelected: {
    color: 'rgba(0, 0, 0, 0.4)',
    fontSize: '12px',
    fontWeight: 500,
    fontStyle: 'normal',
    lineHeight: '15px',
    padding: '0px 16px 8px 0px',
  },
  listSubTextJustify: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  secondaryTextRed: {
    color: 'rgba(255, 0, 0, 1) !important',
  },
  highlightItem: {
    borderRight: `4px solid ${Theme.palette.primary.main}`,
  },
  menuLayout: {
    minWidth: 250,
    padding: '8px 0',
    borderRadius: '8px',
  },
  menuItem: {
    padding: '0px 16px',
    textAlign: 'left',
  },
});

const SidebarListCategory = ({ enableDisplay = false }) => {
  const classes = useStyles();
  const { fetchPartnerTypes, fetchCollectionsByDept } = useCategory();
  const filters = useMemo(() => ({ searchTerm: '', types: ['ROCKED', 'OEM', 'PARTNER'] }), []);
  const [filter, setFilter] = useState({ searchTerm: '', types: ['ROCKED', 'OEM', 'PARTNER'] });
  const [anchorEl, setAnchorEl] = useState(null);
  const selectedId = useSelector((state) => state.categoryData.selectedId);
  const list = useSelector((state) => state.categoryData.categoryList);
  const [filterArray, setFilterArray] = useState([]);

  const handleSearchCategory = useCallback(({ target: { value } }) => {
    setFilter((prev) => ({ ...prev, searchTerm: value }));
  }, []);

  useEffect(() => {
    fetchPartnerTypes();
  }, [fetchPartnerTypes]);

  useEffect(() => {
    setFilterArray(
      filter.types?.length > 0
        ? list
            .filter(
              (item) =>
                (filter.searchTerm ? item.title?.toLowerCase().includes(filter.searchTerm?.toLowerCase()) : true) &&
                filter.types.includes(item.type),
            )
            .sort((a, b) => {
              if (a.title === 'RockED') return -1;
              if (b.title === 'RockED') return 1;
              return 0;
            })
        : [],
    );
  }, [list, filter]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleFilterChange = ({ target }) => {
    const filterKey = target.name;
    setFilter((prev) => ({
      ...prev,
      [filterKey]: prev[filterKey]?.includes(target.value)
        ? prev[filterKey].filter((item) => item !== target.value)
        : [...prev[filterKey], target.value],
    }));
  };

  const handleChange = (event) => {
    const { checked } = event.target;
    setFilter((prev) => ({
      ...prev,
      types: checked ? [...filters.types] : [],
    }));
  };

  const filterCondition = useMemo(() => filter.types.length === filters.types.length, [filter.types, filters.types]);
  const menuComponent = (
    <Menu
      id="simple-menu"
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleClose}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      className={classes.menuLayout}
    >
      <FormControlLabel
        classes={{ root: classes.fullWidth }}
        label="Select All"
        sx={{
          padding: '0px 16px',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          '& .MuiFormControlLabel-label': {
            fontSize: '12px',
            fontWeight: '500',
            color: () =>
              filterCondition || (filter.types.length > 0 && filter.types.length < filters.types.length)
                ? '#10182B'
                : '#667085',
          },
        }}
        control={
          <Checkbox
            checked={filterCondition}
            indeterminate={filter.types.length > 0 && filter.types.length < filters.types.length}
            onChange={handleChange}
            size="small"
            sx={{
              color: '#5925DC',
              '&.Mui-checked': {
                color: '#5925DC',
              },
              '&.MuiCheckbox-indeterminate': {
                color: '#5925DC',
              },
            }}
          />
        }
      />

      {Object.keys(filters).map((key) => (
        <div key={key}>
          {Array.isArray(filters[key]) &&
            filters[key].map((ele) => (
              <MenuItem key={ele} className={classes.menuItem}>
                <FormControlLabel
                  classes={{ root: classes.fullWidth }}
                  label={ele}
                  control={
                    <Checkbox
                      checked={filter[key]?.includes(ele)}
                      name={key}
                      value={ele}
                      onChange={handleFilterChange}
                      size="small"
                      sx={{
                        color: '#5925DC',
                        '&.Mui-checked': {
                          color: '#5925DC',
                        },
                      }}
                    />
                  }
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    width: '100%',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '12px',
                      fontWeight: '500',
                      color: () => (filter[key]?.includes(ele) ? '#10182B' : '#667085'),
                    },
                  }}
                />
              </MenuItem>
            ))}
        </div>
      ))}
    </Menu>
  );
  return (
    <Grid container direction="column">
      <Grid item>
        <SearchBox
          handleSearchChange={handleSearchCategory}
          searchVal={filter.searchTerm}
          filterCondition={filterCondition}
          placeholder="Search Category"
          menu={menuComponent}
          handleClick={handleClick}
        />
      </Grid>
      <Grid item>
        <div className={classes.root}>
          <List component="nav" aria-label="secondary">
            {filterArray.map((data) => (
              <div key={data.id} className={selectedId === data.id ? classes.highlightItem : ''}>
                <ListItem button selected={selectedId === data.id} onClick={() => fetchCollectionsByDept(data.id)}>
                  <ListItemText
                    primary={
                      <p className={selectedId === data.id ? classes.listTextSelected : classes.listTextNonSelected}>
                        {data.title}
                      </p>
                    }
                    secondary={
                      <div
                        className={
                          selectedId === data.id ? classes.listSubTextSelected : classes.listSubTextNonSelected
                        }
                      >
                        <div className={classes.listSubTextJustify}>
                          <div>{`ID: ${data?.id}`}</div>
                          <div>{`${data.collectionCount || 0} Collections`}</div>
                          {enableDisplay && (
                            <div className={!data.enabled ? classes.secondaryTextRed : null}>
                              {data.enabled ? 'Active' : 'Inactive'}
                            </div>
                          )}
                        </div>
                      </div>
                    }
                  />
                </ListItem>
              </div>
            ))}
          </List>
          <ShowProgress />
        </div>
      </Grid>
    </Grid>
  );
};

export default SidebarListCategory;
